import { css } from "@emotion/css";
import { Button, Callout, Divider, ExternalLink } from "@octopusdeploy/design-system-components";
import { borderRadius, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useMutation } from "@octopusdeploy/octopus-react-client";
import type { EnvironmentV2Resource, ProjectResource, Repository } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { useState } from "react";
import { CodeEditor, TextFormat } from "~/components/CodeEditor/CodeEditor";
import SidebarLayout, { SidebarSide } from "~/components/SidebarLayout/SidebarLayout";
import { FormSection, Note } from "~/components/form";
import type { Item } from "~/primitiveComponents/form/Select/Select";
import { VariableScopeSelector } from "./VariableScopeSelector";
interface VariableExpressionTesterTabProps {
    project: ProjectResource;
    environments: EnvironmentV2Resource[];
    environmentId: string | undefined;
    tenants: Item[];
    tenantId: string | undefined;
    actions: Item[];
    actionId: string | undefined;
    channels: Item[];
    channelId: string | undefined;
    machines: Item[];
    machineId: string | undefined;
    gitRef: string | undefined;
    showOctopus: boolean;
    filterEmptyValues: boolean;
    variableNames: string[];
    onEnvironmentChanged: (environmentId: string | undefined) => void;
    onTenantChanged: (tenantId: string | undefined) => void;
    onActionChanged: (actionId: string | undefined) => void;
    onChannelChanged: (channelId: string | undefined) => void;
    onMachineChanged: (machineId: string | undefined) => void;
    onShowOctopusChanged: (showOctopus: boolean) => void;
    onFilterEmptyValuesChanged: (filterEmptyValues: boolean) => void;
}
const styles = {
    container: css({
        display: "flex",
        flexDirection: "column",
        gap: space[12],
    }),
    expressionContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[12],
    }),
    resultSection: css({
        display: "flex",
        flexDirection: "column",
        gap: space[12],
        padding: space[16],
    }),
    resultContainer: css({
        padding: space[12],
        backgroundColor: themeTokens.color.background.secondary.default,
        border: `1px solid ${themeTokens.color.border.primary}`,
        borderRadius: borderRadius["small"],
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
    }),
    resultHeading: css({
        font: text.heading.medium,
        margin: 0,
    }),
};
export function VariableExpressionTesterTab({ project, environments, environmentId, tenants, tenantId, actions, actionId, channels, channelId, machines, machineId, gitRef, showOctopus, filterEmptyValues, variableNames, onEnvironmentChanged, onTenantChanged, onActionChanged, onChannelChanged, onMachineChanged, onShowOctopusChanged, onFilterEmptyValuesChanged, }: VariableExpressionTesterTabProps) {
    const [expression, setExpression] = useState("Deploying #{Octopus.Project.Name} to #{Octopus.Environment.Name}");
    const [evaluationResult, setEvaluationResult] = useState<string | null>(null);
    const [evaluationError, setEvaluationError] = useState<string | null>(null);
    const { execute: evaluateExpression, isExecuting: evaluating } = useMutation({
        name: "Evaluate variable expression",
        action: async (repository: Repository) => {
            const result = await repository.Variables.evaluate(expression, project.Id, actionId, environmentId, machineId, channelId, tenantId, gitRef);
            setEvaluationResult(result.EvaluatedResult);
            setEvaluationError(result.ErrorMessage);
        },
        onError: (error) => {
            setEvaluationResult(null);
            setEvaluationError(`Error: ${error.message}`);
        },
    });
    const sidebar = (<VariableScopeSelector project={project} environments={environments} environmentId={environmentId} tenants={tenants} tenantId={tenantId} actions={actions} actionId={actionId} channels={channels} channelId={channelId} machines={machines} machineId={machineId} showOctopus={showOctopus} filterEmptyValues={filterEmptyValues} onEnvironmentChanged={onEnvironmentChanged} onTenantChanged={onTenantChanged} onActionChanged={onActionChanged} onChannelChanged={onChannelChanged} onMachineChanged={onMachineChanged} onShowOctopusChanged={onShowOctopusChanged} onFilterEmptyValuesChanged={onFilterEmptyValuesChanged}/>);
    const mainContent = (<div className={styles.container}>
            <FormSection title="Variable Expression" help={<>
                        Enter an expression containing <ExternalLink href={"variable-substitution"} label={"variable substitutions"}/>.
                    </>}>
                <Note>The expression will be evaluated using the selected deployment scenario. Sensitive variables will not be included in the evaluation context.</Note>
                <div className={styles.expressionContainer}>
                    <CodeEditor value={expression} onChange={setExpression} language={TextFormat.PlainText} showInsertVariableButton={true} showLineNumbers={false} lineWrapping={true} autoExpand={false} localNames={variableNames}/>
                    <Button importance={"primary"} onClick={evaluateExpression} disabled={!expression.trim() || evaluating} label={evaluating ? "Evaluating..." : "Test"}/>
                </div>
            </FormSection>
            <Divider />
            <div className={styles.resultSection}>
                <h3 className={styles.resultHeading}>Result</h3>
                {evaluationError ? (<Callout title={"There was an error during evaluation"} type={"warning"}>
                        {evaluationError}
                    </Callout>) : null}
                <div className={styles.resultContainer}>{evaluationResult ?? "Test result will be shown here"}</div>
            </div>
        </div>);
    return (<SidebarLayout sideBar={sidebar} side={SidebarSide.Left} extendContentToEdges={true} extendSidebarToEdges={true} hideTopDivider={true} hideSidebarDivider={false} overflowXHidden={true}>
            {mainContent}
        </SidebarLayout>);
}
